import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight, faCheck } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";

export interface StepperStep {
  id: string;
  label: string;
  completed?: boolean;
}

export interface StepperProps {
  steps: StepperStep[];
  activeStepId: string;
  /** Whether the user may jump to this step (ignored for the active step). */
  isStepEnabled?: (step: StepperStep, index: number) => boolean;
  onStepClick?: (stepId: string) => void;
  className?: string;
  "data-testid"?: string;
}

/**
 * Compact horizontal stepper (numbered steps + labels, chevron separators).
 * The active step is indicated by an indigo-filled index circle only; labels
 * stay on the neutral track (Meetings-style outer bar, no full-step pill).
 */
export function Stepper({
  steps,
  activeStepId,
  isStepEnabled = () => true,
  onStepClick,
  className,
  "data-testid": dataTestId,
}: StepperProps) {
  if (steps.length <= 1) return null;

  return (
    <div
      className={clsx(
        "flex flex-wrap items-center gap-0.5 rounded-lg bg-slate-100 p-1 dark:bg-slate-800",
        className
      )}
      data-testid={dataTestId}
      role="navigation"
      aria-label="Steps"
    >
      {steps.map((s, idx) => {
        const isActive = s.id === activeStepId;
        const enabled = isStepEnabled(s, idx);
        const clickable = Boolean(onStepClick && !isActive && enabled);

        return (
          <div key={s.id} className="flex items-center">
            <button
              type="button"
              disabled={!isActive && !clickable}
              onClick={() => clickable && onStepClick?.(s.id)}
              className={clsx(
                "flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors focus:outline-none",
                isActive &&
                  "cursor-default text-slate-900 dark:text-white",
                !isActive &&
                  clickable &&
                  "cursor-pointer text-slate-600 hover:bg-white/80 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-white/10 dark:hover:text-slate-100",
                !isActive &&
                  !clickable &&
                  "cursor-default text-slate-400 dark:text-slate-500"
              )}
            >
              <span
                className={clsx(
                  "inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold tabular-nums",
                  isActive &&
                    "bg-indigo-600 text-white shadow-sm dark:bg-indigo-500 dark:text-white",
                  !isActive &&
                    s.completed &&
                    "bg-emerald-500/15 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-400",
                  !isActive &&
                    !s.completed &&
                    clickable &&
                    "bg-slate-300 text-slate-800 dark:bg-slate-600 dark:text-slate-100",
                  !isActive &&
                    !s.completed &&
                    !clickable &&
                    "bg-slate-200/80 text-slate-500 dark:bg-slate-900 dark:text-slate-500"
                )}
              >
                {s.completed ? (
                  <FontAwesomeIcon icon={faCheck} className="text-[9px]" />
                ) : (
                  idx + 1
                )}
              </span>
              <span className="whitespace-nowrap">{s.label}</span>
            </button>
            {idx < steps.length - 1 && (
              <FontAwesomeIcon
                icon={faChevronRight}
                className="mx-0.5 text-[9px] text-slate-400 dark:text-slate-500"
                aria-hidden
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
